import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Globe2, X } from "lucide-react";

/**
 * Shown on Overview only when the detected market currency in real CSVs
 * disagrees with the workspace's declared currency. One-click switch
 * writes to analysis_context so every downstream prompt / UI flips.
 */
export default function CurrencyMismatchBanner() {
  const [signal, setSignal] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    // Dismissed locally for the browser session — never for other users.
    if (sessionStorage.getItem("dara-ccy-banner-dismissed") === "1") {
      setDismissed(true);
      return;
    }
    api.get("/workspace/currency-signal")
      .then((r) => setSignal(r.data))
      .catch(() => setSignal(null));
  }, []);

  if (dismissed || !signal || !signal.mismatch) return null;
  const d = signal.detected;
  const c = signal.current;

  const handleSwitch = async () => {
    setSwitching(true);
    try {
      await api.patch("/workspace", {
        analysis_context: {
          country: d.country,
          currency_code: d.currency_code,
          currency_symbol: d.currency_symbol,
        },
      });
      toast.success(`Workspace switched to ${d.currency_code} (${d.currency_symbol}).`);
      // Reload so every page / ticker / KPI refreshes with the new symbol.
      window.location.reload();
    } catch {
      toast.error("Couldn't switch currency. Are you the workspace owner?");
      setSwitching(false);
    }
  };

  const dismiss = () => {
    sessionStorage.setItem("dara-ccy-banner-dismissed", "1");
    setDismissed(true);
  };

  return (
    <div
      data-testid="currency-mismatch-banner"
      className="border border-[#FFB000]/40 bg-[#FFB000]/5 p-4 flex items-start gap-4"
    >
      <Globe2 className="w-5 h-5 text-[#FFB000] flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-1">
          Localization signal detected
        </div>
        <p className="text-sm text-zinc-200 leading-relaxed">
          Your data looks like <strong className="text-white">{d.country}</strong>{" "}
          ({d.currency_code} {d.currency_symbol}) across{" "}
          <strong className="text-white">{d.source_count} source{d.source_count > 1 ? "s" : ""}</strong>
          {c?.currency_code ? (
            <> — but your workspace is configured for <strong className="text-white">{c.currency_code}</strong>.</>
          ) : (
            <> — but your workspace has no currency set yet.</>
          )}{" "}
          Switch the workspace so every insight, briefing, and scorecard renders in {d.currency_symbol}.
        </p>
        {signal.evidence?.length ? (
          <div className="mt-2 font-mono text-[10px] text-zinc-500">
            evidence: {signal.evidence.map((e) => e.source_name).join(" · ")}
          </div>
        ) : null}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={handleSwitch}
          disabled={switching}
          data-testid="switch-currency-btn"
          className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-4 py-2 hover:bg-[#E59E00] disabled:opacity-50"
        >
          {switching ? "SWITCHING…" : `Switch to ${d.currency_code} ${d.currency_symbol}`}
        </button>
        <button
          onClick={dismiss}
          data-testid="dismiss-currency-banner"
          aria-label="Dismiss banner"
          className="text-zinc-500 hover:text-white p-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
